import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import axios from 'axios';
import { API_BASE } from '../lib/utils';
import { Search, MapPin, Briefcase, ArrowRight, Building2 } from 'lucide-react';

const ApprenticeshipSearch = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API_BASE}/apprenticeships/categories`),
        axios.get(`${API_BASE}/apprenticeships/locations`)
      ]);
      setCategories(categoriesRes.data.categories);
      setLocations(locationsRes.data.locations);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/apprenticeships/search`, {
        params: {
          category: selectedCategory || undefined,
          location: selectedLocation || undefined
        }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    navigate('/login');
  };

  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white" id="search-apprenticeships">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-[#1d1d1f] mb-4">
            Explore apprenticeships
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-[#86868b] mb-2">
            Search from <span className="font-semibold text-[#1d1d1f]">110 current openings</span> and <span className="font-semibold text-[#1d1d1f]">1,630+ employers</span>
          </p>
          <p className="text-base sm:text-lg text-[#86868b]">
            Real apprenticeship opportunities across Ireland
          </p>
        </div>

        {/* Search Filters */}
        <div className="bg-[#f5f5f7] rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Category Dropdown */}
            <div>
              <label className="block text-sm font-semibold text-[#1d1d1f] mb-2">
                <Briefcase className="w-4 h-4 inline mr-2" />
                Job Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-[#d2d2d7] bg-white text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-[#0071e3]"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Location Dropdown */}
            <div>
              <label className="block text-sm font-semibold text-[#1d1d1f] mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Location
              </label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full h-12 px-4 rounded-xl border border-[#d2d2d7] bg-white text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-[#0071e3]"
              >
                <option value="">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-xl h-12 font-normal"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          {searchResults && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-6 border-t border-[#d2d2d7]">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-semibold text-[#0071e3]">
                  {searchResults.stats.total_current_jobs}
                </div>
                <div className="text-xs sm:text-sm text-[#86868b]">Current Openings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-semibold text-[#0071e3]">
                  {searchResults.stats.total_employers}
                </div>
                <div className="text-xs sm:text-sm text-[#86868b]">Active Employers</div>
              </div>
              <div className="text-center col-span-2 sm:col-span-1">
                <div className="text-3xl sm:text-4xl font-semibold text-[#0071e3]">
                  {searchResults.stats.total_positions}
                </div>
                <div className="text-xs sm:text-sm text-[#86868b]">Total Positions</div>
              </div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="space-y-6">
            {/* Current Job Openings */}
            {searchResults.current_jobs.length > 0 && (
              <div>
                <h3 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] mb-4">
                  Current Job Openings ({searchResults.current_jobs.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {searchResults.current_jobs.map((job) => (
                    <div
                      key={job.id}
                      className="bg-white border border-[#d2d2d7] rounded-2xl p-6 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-xl font-semibold text-[#1d1d1f] mb-1 break-words">
                            {job.title}
                          </h4>
                          <p className="text-base text-[#86868b] flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {job.employer}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-[#86868b] mb-4">
                        <MapPin className="w-4 h-4 mr-1" />
                        {job.location}
                        <span className="mx-2">•</span>
                        <span className="text-green-600 font-semibold">{job.status}</span>
                      </div>
                      <Button
                        onClick={handleApply}
                        className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full"
                      >
                        Apply For This Role Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Employers */}
            {searchResults.active_employers.length > 0 && (
              <div>
                <h3 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] mb-4">
                  Active Employers Hiring ({searchResults.active_employers.length}+)
                </h3>
                <div className="bg-white border border-[#d2d2d7] rounded-2xl p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {searchResults.active_employers.slice(0, 6).map((employer, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-[#f5f5f7] rounded-xl">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1d1d1f] truncate">{employer.employer}</p>
                          <p className="text-sm text-[#86868b]">{employer.location}</p>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-2xl font-bold text-[#0071e3]">{employer.total_positions}</p>
                          <p className="text-xs text-[#86868b]">positions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={handleApply}
                    className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full text-lg py-6 h-auto"
                  >
                    Sign Up to Match with These Employers
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* No Results */}
            {searchResults.current_jobs.length === 0 && searchResults.active_employers.length === 0 && (
              <div className="text-center py-12 bg-[#f5f5f7] rounded-2xl">
                <p className="text-xl text-[#86868b] mb-4">No results found for this combination</p>
                <p className="text-base text-[#86868b] mb-6">Try selecting different filters or view all opportunities</p>
                <Button
                  onClick={handleApply}
                  className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8"
                >
                  View All Opportunities
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Initial CTA (before search) */}
        {!searchResults && (
          <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl sm:rounded-3xl">
            <p className="text-xl sm:text-2xl text-[#1d1d1f] mb-6">
              Select a category or location to explore opportunities
            </p>
            <Button
              onClick={handleApply}
              className="bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-full px-8 py-6 h-auto text-lg"
            >
              Sign Up to Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ApprenticeshipSearch;
